import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  createInterest,
  updateInterest,
  type ProductCategory,
  type ProductInterest,
} from '@/api/productCatalog'
import { useApiErrorMessage } from '@/api/errors'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export function InterestDialog({
  open,
  interest,
  categories,
  defaultCategoryId,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  /** Null creates an interest in [defaultCategoryId]; one edits it. */
  interest: ProductInterest | null
  categories: ProductCategory[]
  defaultCategoryId: string | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const categoryId = interest?.categoryId ?? defaultCategoryId ?? categories[0]?.id ?? null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {interest ? t('catalog.interest.editTitle') : t('catalog.interest.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('catalog.interest.description')}</DialogDescription>
        </DialogHeader>
        {open && categoryId && (
          <InterestForm
            key={interest?.id ?? `new-${categoryId}`}
            interest={interest}
            categories={categories}
            initialCategoryId={categoryId}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function InterestForm({
  interest,
  categories,
  initialCategoryId,
  onOpenChange,
  onSaved,
}: {
  interest: ProductInterest | null
  categories: ProductCategory[]
  initialCategoryId: string
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()

  const [categoryId, setCategoryId] = useState(initialCategoryId)
  const [name, setName] = useState(interest?.name ?? '')
  const [active, setActive] = useState(interest?.active ?? true)
  const [error, setError] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const moving = interest !== null && categoryId !== interest.categoryId

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting) {
      return
    }
    if (!name.trim()) {
      setError(t('validation.required'))
      return
    }
    setError(null)
    setSubmitting(true)
    setFailure(null)
    const body = { categoryId, name: name.trim(), active }
    try {
      if (interest) {
        await updateInterest(interest.id, body)
        toast.success(t('catalog.interest.updated', { name: body.name }))
      } else {
        await createInterest(body)
        toast.success(t('catalog.interest.created', { name: body.name }))
      }
      onSaved()
      onOpenChange(false)
    } catch (caught) {
      setFailure(errorMessage(caught, t('catalog.interest.failed')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="grid gap-4 py-2">
        {failure && (
          <Alert variant="destructive">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-2">
          <Label htmlFor="interest-category">{t('catalog.interest.category')}</Label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger id="interest-category" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                  {!category.active && ` (${t('catalog.retired')})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Changing the category *is* the move. Worth saying, because the position changes with
              it and an unexplained jump in the list reads as a bug. */}
          {moving && (
            <p className="text-muted-foreground text-xs">{t('catalog.interest.moveHint')}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="interest-name">{t('catalog.interest.name')}</Label>
          <Input
            id="interest-name"
            value={name}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'interest-name-error' : 'interest-name-hint'}
            onChange={(event) => setName(event.target.value)}
          />
          {error ? (
            <p id="interest-name-error" className="text-destructive text-xs">
              {error}
            </p>
          ) : (
            <p id="interest-name-hint" className="text-muted-foreground text-xs">
              {t('catalog.renameHint')}
            </p>
          )}
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id="interest-active"
            checked={active}
            onCheckedChange={(checked) => setActive(checked === true)}
          />
          <div className="grid gap-1">
            <Label htmlFor="interest-active" className="font-normal">
              {t('catalog.activeLabel')}
            </Label>
            <p className="text-muted-foreground text-xs">{t('catalog.activeHint')}</p>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={submitting}>
          {interest ? t('catalog.save') : t('catalog.create')}
        </Button>
      </DialogFooter>
    </form>
  )
}
